/**
 * Utilitários para internacionalização (i18n)
 * Funções para lidar com conteúdo multilíngue
 */

/**
 * Obtém o nome localizado de uma categoria baseado no idioma atual
 * @param {Object} category - Objeto da categoria com namePt, nameEn, nameEs
 * @param {string} language - Código do idioma (pt, en, es)
 * @returns {string} Nome da categoria no idioma especificado
 */
export const getCategoryLocalizedName = (category, language = 'en') => {
  if (!category) return ''
  
  // Normalizar o código do idioma
  const normalizedLang = language?.toLowerCase()
  
  switch (normalizedLang) {
    case 'pt':
    case 'pt-br':
    case 'br':
      return category.name_pt || category.namePt || category.name || 'Sem nome'
    case 'es':
    case 'es-es':
      return category.name_es || category.nameEs || category.name || 'Sin nombre'
    case 'en':
    case 'en-us':
    default:
      return category.name_en || category.nameEn || category.name || 'No name'
  }
}

/**
 * Obtém o texto localizado de um objeto multilíngue genérico
 * @param {Object} item - Objeto com propriedades multilíngues
 * @param {string} field - Campo base (ex: 'name', 'description')
 * @param {string} language - Código do idioma
 * @param {string} fallback - Texto de fallback
 * @returns {string} Texto localizado
 */
export const getLocalizedText = (item, field, language = 'en', fallback = '') => {
  if (!item || !field) return fallback
  
  const normalizedLang = language?.toLowerCase()
  
  // Tentar obter o campo localizado
  let localizedField
  switch (normalizedLang) {
    case 'pt':
    case 'pt-br':
    case 'br':
      localizedField = `${field}Pt`
      break
    case 'es':
    case 'es-es':
      localizedField = `${field}Es`
      break
    case 'en':
    case 'en-us':
    default:
      localizedField = `${field}En`
      break
  }
  
  // Retornar na seguinte ordem de prioridade:
  // 1. Campo localizado específico
  // 2. Campo base
  // 3. Fallback
  return item[localizedField] || item[field] || fallback
}

/**
 * Mapeia códigos de idioma do i18next para sufixos de campos
 * @param {string} language - Código do idioma do i18next
 * @returns {string} Sufixo do campo (Pt, En, Es)
 */
export const getFieldSuffix = (language = 'en') => {
  const normalizedLang = language?.toLowerCase()
  
  switch (normalizedLang) {
    case 'pt':
    case 'pt-br':
    case 'br':
      return 'Pt'
    case 'es':
    case 'es-es':
      return 'Es'
    case 'en':
    case 'en-us':
    default:
      return 'En'
  }
}

/**
 * Obtém o texto de fallback padrão baseado no idioma
 * @param {string} language - Código do idioma
 * @param {string} type - Tipo de fallback ('name', 'description', etc.)
 * @returns {string} Texto de fallback
 */
export const getDefaultFallback = (language = 'en', type = 'name') => {
  const normalizedLang = language?.toLowerCase()
  
  const fallbacks = {
    pt: {
      name: 'Sem nome',
      description: 'Sem descrição',
      title: 'Sem título'
    },
    es: {
      name: 'Sin nombre',
      description: 'Sin descripción',
      title: 'Sin título'
    },
    en: {
      name: 'No name',
      description: 'No description',
      title: 'No title'
    }
  }
  
  const langFallbacks = fallbacks[normalizedLang] || fallbacks.en
  return langFallbacks[type] || langFallbacks.name
}

/**
 * Hook personalizado para obter textos localizados (para uso em componentes React)
 * @param {string} language - Código do idioma atual
 * @returns {Object} Objeto com funções utilitárias
 */
export const getLocalizedProductName = (product, language = 'en') => {
  if (!product) return ''
  const normalizedLang = language?.toLowerCase()
  switch (normalizedLang) {
    case 'pt':
    case 'pt-br':
    case 'br':
      return product.namePt || product.name_en || product.namePtBr || product.name || ''
    case 'es':
    case 'es-es':
      return product.nameEs || product.name_es || product.name || ''
    case 'en':
    case 'en-us':
    default:
      return product.nameEn || product.name_en || product.name || ''
  }
}

export const getLocalizedProductDescription = (product, language = 'en') => {
  if (!product) return ''
  const normalizedLang = language?.toLowerCase()
  switch (normalizedLang) {
    case 'pt':
    case 'pt-br':
    case 'br':
      return product.descriptionPt || product.description || ''
    case 'es':
    case 'es-es':
      return product.descriptionEs || product.description || ''
    case 'en':
    case 'en-us':
    default:
      return product.descriptionEn || product.description || ''
  }
}

export const useLocalizedContent = (language) => {
  return {
    getCategoryName: (category) => getCategoryLocalizedName(category, language),
    getText: (item, field, fallback) => getLocalizedText(item, field, language, fallback),
    getFieldSuffix: () => getFieldSuffix(language),
    getFallback: (type) => getDefaultFallback(language, type)
  }
}
